// client/src/components/pages/LexiStar/Dwell/DwellHistogram.tsx
//
// One context's dwell distribution at a site (T7-S04): the API's histogram as
// columns on a minutes axis, the median as a tick and the site's limit as a
// dashed line. Visits past the limit take the LS-09 §4 delay colour (orange —
// "dwell over limit" is a state in the status language); everything else is
// neutral ink. Each column carries its bin and count for hover and AT, and the
// row beside it states median / p90 / over-limit in text.

import type { DwellDistribution } from '../../../../types/lexistar-analytics';
import { binEnd, fmtMin } from './dwellLogic';

const W = 240;
const H = 44;
const PAD_B = 12;
const GAP = 1.5;

export default function DwellHistogram({
  d,
  max,
  label,
  className = '',
}: {
  d: DwellDistribution;
  max: number;
  label: string;
  className?: string;
}) {
  const bins = d.histogram || [];
  const top = Math.max(1, ...bins.map((b) => b.visits));
  const x = (m: number) => (Math.min(m, max) / max) * W;
  const plotH = H - PAD_B;
  const limit = d.limit_min;
  const summary = `${label}: ${d.visits} visits, median ${fmtMin(d.p50_min)}, 90% within ${fmtMin(d.p90_min)}${
    limit !== null ? `, limit ${fmtMin(limit)}, ${d.over_limit_visits} over` : ', no limit set'
  }`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`w-full h-11 overflow-visible ${className}`}
      role="img"
      aria-label={summary}
      preserveAspectRatio="none"
      data-testid="dwell-histogram"
    >
      <title>{summary}</title>
      <line x1={0} x2={W} y1={plotH + 0.5} y2={plotH + 0.5} className="stroke-slate-300" strokeWidth={1} />
      {bins.map((b, i) => {
        const x0 = x(b.from_min);
        const x1 = x(binEnd(bins, i));
        const h = b.visits ? Math.max(1.5, (b.visits / top) * (plotH - 2)) : 0;
        const over = limit !== null && b.from_min >= limit;
        return (
          <rect
            key={i}
            x={x0 + GAP / 2}
            width={Math.max(0.5, x1 - x0 - GAP)}
            y={plotH - h}
            height={h}
            rx={1.5}
            className={over ? 'fill-orange-500' : 'fill-slate-400'}
          >
            <title>{`${fmtMin(b.from_min)}–${b.to_min === null ? '…' : fmtMin(b.to_min)}: ${b.visits} visit${b.visits === 1 ? '' : 's'}`}</title>
          </rect>
        );
      })}
      {d.p50_min !== null && (
        <line
          x1={x(d.p50_min)}
          x2={x(d.p50_min)}
          y1={plotH + 1}
          y2={plotH + 6}
          className="stroke-slate-900"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {limit !== null && (
        <line
          x1={x(limit)}
          x2={x(limit)}
          y1={0}
          y2={plotH + 6}
          className="stroke-slate-700"
          strokeWidth={1.25}
          strokeDasharray="3 2"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}
